import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import {
  useCountryLanguageMappings,
  useCreateCountryLanguageMapping,
  useDeleteCountryLanguageMapping,
  useUpdateCountryLanguageMapping,
} from '../hooks/useAdminData';

/**
 * FLOSEAI-367 — dialing prefix to agent language, administrable.
 *
 * The backend CRUD shipped in PR #138 and this screen never did, so QA looked
 * for it across four rounds and navigating straight to the route redirected to
 * Overview without a message. It is also what unblocks FLOSEAI-124: inferring
 * an agent's language from the lead's country prefix has no source of data
 * until this table can be edited.
 */

// Mirrors the VoiceAgentLanguage enum. Kept explicit rather than derived so a
// value the backend does not accept cannot be offered here.
const LANGUAGES = ['es_CO', 'es_MX', 'es_LATAM', 'en_US', 'pt_BR'] as const;

const DIALING_PREFIX = /^\+\d{1,4}$/;
const COUNTRY_ISO = /^[A-Z]{2}$/;

export function VoiceLanguageMappings() {
  const { data: mappings, isLoading } = useCountryLanguageMappings();
  const create = useCreateCountryLanguageMapping();
  const update = useUpdateCountryLanguageMapping();
  const remove = useDeleteCountryLanguageMapping();

  const [countryCode, setCountryCode] = useState('');
  const [countryIso, setCountryIso] = useState('');
  const [language, setLanguage] = useState<string>(LANGUAGES[0]);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  function notify(type: 'success' | 'error', message: string) {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  }

  // The API enforces both shapes, so validating here turns a typo into a
  // readable message instead of a 400 the admin has to decode.
  function handleCreate() {
    if (!DIALING_PREFIX.test(countryCode)) {
      setFormError('The dialing prefix must look like +57 — a plus sign and 1 to 4 digits.');
      return;
    }

    if (!COUNTRY_ISO.test(countryIso)) {
      setFormError('The country ISO must be exactly two uppercase letters, like CO.');
      return;
    }

    setFormError(null);

    create.mutate(
      { countryCode, language, countryIso },
      {
        onSuccess: () => {
          notify('success', `Mapping for ${countryCode} created`);
          setCountryCode('');
          setCountryIso('');
        },
        onError: () => notify('error', `Could not create the mapping for ${countryCode}`),
      },
    );
  }

  function handleLanguageChange(code: string, nextLanguage: string) {
    update.mutate(
      { countryCode: code, language: nextLanguage },
      {
        onSuccess: () => notify('success', `${code} now maps to ${nextLanguage}`),
        onError: () => notify('error', `Could not update ${code}`),
      },
    );
  }

  function handleDelete(code: string) {
    remove.mutate(
      { countryCode: code },
      {
        onSuccess: () => {
          notify('success', `Mapping for ${code} deleted`);
          setConfirmingDelete(null);
        },
        onError: () => notify('error', `Could not delete ${code}`),
      },
    );
  }

  const rows = mappings ?? [];

  return (
    <div className="p-6 space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Voice language mappings</h1>
        <p className="text-sm text-slate-500">
          Which language a voice agent speaks when it dials a lead in a given country. Voice agents
          fall back to their own configured language when a prefix is not listed here.
        </p>
      </header>

      {notification ? (
        <div
          data-testid="mapping-notification"
          className={
            notification.type === 'success'
              ? 'rounded border border-green-300 bg-green-50 p-3 text-sm text-green-800'
              : 'rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800'
          }
        >
          {notification.message}
        </div>
      ) : null}

      <section className="space-y-3 rounded border border-slate-200 p-4">
        <h2 className="font-medium">Add a mapping</h2>

        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col text-sm">
            Dialing prefix
            <input
              data-testid="mapping-country-code"
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              placeholder="+57"
              className="mt-1 rounded border border-slate-300 px-2 py-1"
            />
          </label>

          <label className="flex flex-col text-sm">
            Country ISO
            <input
              data-testid="mapping-country-iso"
              value={countryIso}
              onChange={(e) => setCountryIso(e.target.value.toUpperCase())}
              placeholder="CO"
              className="mt-1 rounded border border-slate-300 px-2 py-1"
            />
          </label>

          <label className="flex flex-col text-sm">
            Language
            <select
              data-testid="mapping-language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="mt-1 rounded border border-slate-300 px-2 py-1"
            >
              {LANGUAGES.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            data-testid="mapping-create"
            onClick={handleCreate}
            disabled={create.isPending}
            className="rounded bg-slate-900 px-3 py-1.5 text-sm text-white disabled:opacity-50"
          >
            Add mapping
          </button>
        </div>

        {formError ? (
          <p data-testid="mapping-form-error" className="text-sm text-red-700">
            {formError}
          </p>
        ) : null}
      </section>

      {isLoading ? (
        <p className="text-sm text-slate-500">Loading mappings…</p>
      ) : rows.length === 0 ? (
        <p data-testid="mappings-empty" className="text-sm text-slate-500">
          No mappings yet. Until at least one exists, language inference by country has nothing to
          read and every agent falls back to its own configured language.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              <th className="py-2">Dialing prefix</th>
              <th className="py-2">Country ISO</th>
              <th className="py-2">Language</th>
              <th className="py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((mapping) => (
              <tr key={mapping.countryCode} className="border-b border-slate-100">
                <td className="py-2 font-mono">{mapping.countryCode}</td>
                <td className="py-2">{mapping.countryIso}</td>
                <td className="py-2">
                  <select
                    data-testid={`mapping-language-${mapping.countryCode}`}
                    value={mapping.language}
                    onChange={(e) => handleLanguageChange(mapping.countryCode, e.target.value)}
                    className="rounded border border-slate-300 px-2 py-1"
                  >
                    {LANGUAGES.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="py-2">
                  {confirmingDelete === mapping.countryCode ? (
                    <span className="flex items-center gap-2">
                      <button
                        type="button"
                        data-testid={`mapping-delete-confirm-${mapping.countryCode}`}
                        onClick={() => handleDelete(mapping.countryCode)}
                        className="rounded bg-red-600 px-2 py-1 text-xs text-white"
                      >
                        Delete
                      </button>
                      <button
                        type="button"
                        data-testid={`mapping-delete-cancel-${mapping.countryCode}`}
                        onClick={() => setConfirmingDelete(null)}
                        className="rounded border border-slate-300 px-2 py-1 text-xs"
                      >
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      data-testid={`mapping-delete-${mapping.countryCode}`}
                      onClick={() => setConfirmingDelete(mapping.countryCode)}
                      aria-label={`Delete mapping for ${mapping.countryCode}`}
                      className="text-slate-500 hover:text-red-600"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
